import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, TrendingUp, DollarSign, ShoppingBag, Users, BarChart2 } from 'lucide-react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend,
} from 'recharts';
import kitchenApi from '../services/kitchenApiService';

const PERIOD_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'week',  label: 'This Week' },
  { value: 'month', label: 'This Month' },
  { value: 'year',  label: 'This Year' },
];

const PIE_COLORS = ['#FF6B35', '#8B5CF6', '#10B981', '#F59E0B', '#3B82F6', '#EC4899'];
const fmt$ = (n) => `$${(n || 0).toFixed(2)}`;

function StatCard({ label, value, trend, trendLabel, icon: Icon, color, bg }) {
  const isUp = trend >= 0;
  return (
    <div className="k-kpi-card">
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div>
          <div className="k-kpi-label">{label}</div>
          <div className="k-kpi-value" style={{ fontSize: 22, marginTop: 6 }}>{value}</div>
        </div>
        <div className="k-kpi-icon-wrap" style={{ background: bg }}>
          <Icon size={20} color={color} />
        </div>
      </div>
      {trend !== undefined && (
        <div className={`k-kpi-trend ${isUp ? 'up' : 'down'}`} style={{ marginTop: 6 }}>
          {isUp ? '↑' : '↓'} {Math.abs(trend)}% {trendLabel || 'vs last period'}
        </div>
      )}
    </div>
  );
}

export default function AnalyticsPage() {
  const [period,    setPeriod]    = useState('month');
  const [tab,       setTab]       = useState('revenue');
  const [loading,   setLoading]   = useState(true);
  const [finData,   setFinData]   = useState(null);
  const [perfData,  setPerfData]  = useState(null);
  const [custData,  setCustData]  = useState(null);

  const loadData = useCallback(async (p = period) => {
    setLoading(true);
    const [finR, perfR, custR] = await Promise.all([
      kitchenApi.getFinancialReports(p, 'revenue'),
      kitchenApi.getPerformanceDashboard(p),
      kitchenApi.getCustomerAnalytics(p),
    ]);
    if (finR.success)  setFinData(finR.data);
    if (perfR.success) setPerfData(perfR.data);
    if (custR.success) setCustData(custR.data);
    setLoading(false);
  }, [period]);

  useEffect(() => { loadData(period); }, [period]);

  const fd = finData || {};
  const pd = perfData || {};
  const cd = custData || {};

  const summary    = fd.summary || {};
  const revChart   = fd.daily_breakdown || fd.revenue_chart || [];
  const revByItem  = fd.revenue_breakdown || [];
  const expBreak   = fd.expenses_breakdown || [];
  const orderStats = pd.order_stats || {};
  const menuPerf   = pd.menu_performance || pd.top_items || [];

  return (
    <div>
      <div className="k-page-header">
        <div>
          <h1 className="k-page-title">Analytics & Reports</h1>
          <p className="k-page-sub">Insights into your kitchen performance</p>
        </div>
        <div className="k-page-actions">
          <select className="k-select" style={{ width: 160 }} value={period} onChange={e => setPeriod(e.target.value)}>
            {PERIOD_OPTIONS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <button className="k-btn-icon" onClick={() => loadData(period)} title="Refresh">
            <RefreshCw size={16} />
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="k-tabs" style={{ marginBottom: 20, maxWidth: 480 }}>
        <button className={`k-tab${tab === 'revenue' ? ' active' : ''}`} onClick={() => setTab('revenue')}><DollarSign size={15} /> Revenue</button>
        <button className={`k-tab${tab === 'orders' ? ' active' : ''}`} onClick={() => setTab('orders')}><ShoppingBag size={15} /> Orders</button>
        <button className={`k-tab${tab === 'menu' ? ' active' : ''}`} onClick={() => setTab('menu')}><BarChart2 size={15} /> Menu</button>
        <button className={`k-tab${tab === 'customers' ? ' active' : ''}`} onClick={() => setTab('customers')}><Users size={15} /> Customers</button>
      </div>

      {loading ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
          {Array(4).fill(0).map((_, i) => <div key={i} className="k-card"><div className="k-skeleton" style={{ height: 80 }} /></div>)}
        </div>
      ) : (
        <>
          {/* ── Revenue ── */}
          {tab === 'revenue' && (
            <div>
              <div className="k-kpi-grid">
                <StatCard label="Total Revenue"   value={fmt$(summary.total_revenue)}  trend={summary.revenue_trend}  icon={DollarSign}  color="#FF6B35" bg="#FFF7F3" />
                <StatCard label="Total Expenses"  value={fmt$(summary.total_expenses)} trend={summary.expenses_trend} icon={TrendingUp}   color="#EF4444" bg="#FEF2F2" />
                <StatCard label="Net Profit"      value={fmt$(summary.net_profit)}     trend={summary.profit_trend}   icon={DollarSign}   color="#10B981" bg="#ECFDF5" />
                <StatCard label="Profit Margin"   value={`${(summary.profit_margin || 0).toFixed(1)}%`} trend={summary.margin_trend} icon={BarChart2} color="#8B5CF6" bg="#F5F3FF" />
              </div>

              <div className="k-grid-2" style={{ marginBottom: 20 }}>
                <div className="k-card">
                  <div className="k-card-title" style={{ marginBottom: 16 }}>Revenue Trend</div>
                  {revChart.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <AreaChart data={revChart} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                        <defs>
                          <linearGradient id="rg" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%"  stopColor="#FF6B35" stopOpacity={0.25} />
                            <stop offset="95%" stopColor="#FF6B35" stopOpacity={0} />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                        <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                        <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} tickFormatter={v => `$${v}`} />
                        <Tooltip formatter={v => [`$${(v||0).toFixed(2)}`, 'Revenue']} />
                        <Area type="monotone" dataKey="revenue" stroke="#FF6B35" strokeWidth={2.5} fill="url(#rg)" />
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : <div className="k-empty" style={{ padding: '30px 0' }}><div className="k-empty-icon">📈</div><div className="k-empty-title">No revenue data</div></div>}
                </div>

                <div className="k-card">
                  <div className="k-card-title" style={{ marginBottom: 16 }}>Revenue by Category</div>
                  {revByItem.length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <PieChart>
                        <Pie data={revByItem} dataKey="amount" nameKey="category" cx="50%" cy="50%" outerRadius={75} label={e => `${e.category} ${e.percentage?.toFixed(0)}%`} labelLine={false}>
                          {revByItem.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                        </Pie>
                        <Tooltip formatter={v => [`$${(v||0).toFixed(2)}`, 'Revenue']} />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : <div className="k-empty" style={{ padding: '30px 0' }}><div className="k-empty-icon">🥧</div><div className="k-empty-title">No breakdown data</div></div>}
                </div>
              </div>

              {/* Daily table */}
              {revChart.length > 0 && (
                <div className="k-card">
                  <div className="k-card-title" style={{ marginBottom: 12 }}>Daily Breakdown</div>
                  <div className="k-table-wrap">
                    <table className="k-table">
                      <thead><tr><th>Date</th><th>Orders</th><th>Revenue</th><th>Profit</th></tr></thead>
                      <tbody>
                        {revChart.map((row, i) => (
                          <tr key={i}>
                            <td>{row.date}</td>
                            <td>{row.orders}</td>
                            <td style={{ fontWeight: 600 }}>{fmt$(row.revenue)}</td>
                            <td style={{ color: 'var(--k-success)', fontWeight: 600 }}>{row.profit ? fmt$(row.profit) : '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── Orders ── */}
          {tab === 'orders' && (
            <div>
              <div className="k-kpi-grid">
                {[
                  { label: 'Total Orders',    value: (orderStats.total_orders ?? pd.total_orders ?? 0).toLocaleString() },
                  { label: 'Completed',       value: (orderStats.completed ?? 0).toLocaleString() },
                  { label: 'Cancelled',       value: (orderStats.cancelled ?? 0).toLocaleString() },
                  { label: 'Avg Order Value', value: fmt$(orderStats.avg_order_value ?? pd.avg_order_value) },
                  { label: 'Avg Prep Time',   value: `${orderStats.avg_prep_time ?? pd.avg_prep_time ?? '—'} min` },
                ].map(({ label, value }) => (
                  <div key={label} className="k-kpi-card">
                    <div className="k-kpi-label">{label}</div>
                    <div className="k-kpi-value" style={{ fontSize: 24, marginTop: 6 }}>{value}</div>
                  </div>
                ))}
              </div>
              <div className="k-card">
                <div className="k-card-title" style={{ marginBottom: 16 }}>Order Volume Trend</div>
                {revChart.length > 0 ? (
                  <ResponsiveContainer width="100%" height={220}>
                    <BarChart data={revChart} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                      <XAxis dataKey="date" tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                      <YAxis tick={{ fontSize: 11, fill: '#94A3B8' }} axisLine={false} tickLine={false} />
                      <Tooltip />
                      <Bar dataKey="orders" fill="#FF6B35" radius={[4, 4, 0, 0]} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : <div className="k-empty"><div className="k-empty-icon">📊</div><div className="k-empty-title">No order data</div></div>}
              </div>
            </div>
          )}

          {/* ── Menu ── */}
          {tab === 'menu' && (
            <div>
              <div className="k-card">
                <div className="k-card-title" style={{ marginBottom: 16 }}>Top Performing Items</div>
                {menuPerf.length > 0 ? (
                  <div className="k-table-wrap">
                    <table className="k-table">
                      <thead><tr><th>#</th><th>Item</th><th>Orders</th><th>Revenue</th><th>Popularity</th></tr></thead>
                      <tbody>
                        {menuPerf.map((item, i) => (
                          <tr key={i}>
                            <td style={{ fontWeight: 700, color: 'var(--k-text-sub)' }}>#{i + 1}</td>
                            <td style={{ fontWeight: 600 }}>{item.item_name || item.name}</td>
                            <td>{item.order_count || item.orders || '—'}</td>
                            <td style={{ fontWeight: 600 }}>{item.revenue ? fmt$(item.revenue) : '—'}</td>
                            <td>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <div style={{ flex: 1, height: 6, background: '#F1F5F9', borderRadius: 999 }}>
                                  <div style={{ height: '100%', width: `${Math.min(100, item.popularity_score || (i === 0 ? 100 : 70 - i * 8))}%`, background: '#FF6B35', borderRadius: 999 }} />
                                </div>
                                <span style={{ fontSize: 12, color: 'var(--k-text-sub)', minWidth: 28 }}>{item.popularity_score ? `${item.popularity_score}%` : ''}</span>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : <div className="k-empty"><div className="k-empty-icon">🍛</div><div className="k-empty-title">No menu performance data</div></div>}
              </div>
            </div>
          )}

          {/* ── Customers ── */}
          {tab === 'customers' && (
            <div>
              <div className="k-kpi-grid">
                {[
                  { label: 'Total Customers',   value: (cd.total_customers ?? '—') },
                  { label: 'New Customers',      value: (cd.new_customers ?? '—') },
                  { label: 'Returning Rate',     value: cd.returning_rate ? `${cd.returning_rate}%` : '—' },
                  { label: 'Avg Customer Value', value: cd.avg_customer_value ? fmt$(cd.avg_customer_value) : '—' },
                ].map(({ label, value }) => (
                  <div key={label} className="k-kpi-card">
                    <div className="k-kpi-label">{label}</div>
                    <div className="k-kpi-value" style={{ fontSize: 24, marginTop: 6 }}>{value}</div>
                  </div>
                ))}
              </div>
              {!cd.total_customers && (
                <div className="k-card"><div className="k-empty"><div className="k-empty-icon">👥</div><div className="k-empty-title">No customer analytics available</div><div className="k-empty-sub">Customer data will appear as orders come in</div></div></div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
